'use client';

/**
 * KanbanColumn Component
 *
 * A single column (stage) in the Kanban board. Supports WIP limits,
 * droppable area for cards, and neon accent styling.
 */

import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import Box from '@mui/material/Box';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import type { NeonAccent } from '../../theme/palette';
import { getNeonStyles, neonAccents } from '../../theme/palette';
import { createNeonGlow, glassEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import type { KanbanColumn as KanbanColumnType, KanbanItem } from '../../types/pipeline';
import { KanbanCard } from './KanbanCard';

// ============================================================================
// Types
// ============================================================================

export interface KanbanColumnProps<T = unknown> {
  /** Column data */
  column: KanbanColumnType;
  /** Items in this column */
  items: KanbanItem<T>[];
  /** Render function for card content */
  renderCard: (item: KanbanItem<T>) => React.ReactNode;
  /** Card click handler */
  onCardClick?: (item: KanbanItem<T>) => void;
  /** Neon accent color override */
  neonAccent?: NeonAccent;
  /** Whether to show SLA indicators */
  showSLA?: boolean;
  /** Whether to show card count */
  showCount?: boolean;
  /** Allow adding new cards */
  allowAdd?: boolean;
  /** Add card handler */
  onAddCard?: () => void;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledColumnProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $isOver: boolean;
  $isOverWipLimit: boolean;
}

const StyledColumn = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledColumnProps>(({ theme, $isDark, $neonAccent, $isOver, $isOverWipLimit }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  const getBorderColor = () => {
    if ($isOverWipLimit) {
      return neonAccents.red.border;
    }
    if ($isOver) {
      return neonStyles ? neonStyles.base : theme.palette.primary.main;
    }
    return neonStyles ? neonStyles.border : glass.border;
  };

  return {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '280px',
    maxWidth: '320px',
    width: '100%',
    height: '100%',
    borderRadius: '16px',
    background: neonStyles
      ? alpha(neonStyles.background, 0.5)
      : $isDark
        ? 'rgba(255, 255, 255, 0.02)'
        : 'rgba(0, 0, 0, 0.02)',
    border: `1px solid ${getBorderColor()}`,
    boxShadow: $isOver && neonStyles ? createNeonGlow($neonAccent!, 'subtle') : 'none',
    transition: transitionPresets.card,
    overflow: 'hidden',
  };
});

interface ColumnHeaderProps {
  $neonAccent?: NeonAccent;
  $isDark: boolean;
  $isOverWipLimit: boolean;
}

const ColumnHeader = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<ColumnHeaderProps>(({ $neonAccent, $isDark }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderBottom: `1px solid ${$isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
    background: neonStyles ? neonStyles.background : $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
  };
});

const ColumnTitle = styled(Typography)({
  fontWeight: 600,
  fontSize: '14px',
  letterSpacing: '0.01em',
});

interface CountBadgeProps {
  $neonAccent?: NeonAccent;
  $isDark: boolean;
  $isOverLimit: boolean;
}

const CountBadge = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<CountBadgeProps>(({ $neonAccent, $isDark, $isOverLimit }) => {
  const getColors = () => {
    if ($isOverLimit) {
      return {
        bg: neonAccents.red.bg,
        text: neonAccents.red.base,
        border: neonAccents.red.border,
      };
    }
    if ($neonAccent) {
      const colors = neonAccents[$neonAccent];
      return {
        bg: $isDark ? colors.bg : colors.bgLight,
        text: colors.base,
        border: $isDark ? colors.border : colors.borderLight,
      };
    }
    return {
      bg: $isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
      text: $isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
      border: 'transparent',
    };
  };

  const colors = getColors();

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '24px',
    height: '24px',
    padding: '0 8px',
    borderRadius: '12px',
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: '12px',
    fontWeight: 600,
  };
});

const WipIndicator = styled(Typography, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isOver: boolean }>(({ $isOver }) => ({
  fontSize: '11px',
  fontWeight: 500,
  color: $isOver ? neonAccents.red.base : 'inherit',
  opacity: $isOver ? 1 : 0.6,
  marginLeft: '8px',
}));

const CardsContainer = styled(Box)({
  flex: 1,
  padding: '12px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',

  // Custom scrollbar
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(128, 128, 128, 0.3)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: 'rgba(128, 128, 128, 0.5)',
  },
});

const AddCardButton = styled(motion.button, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  width: '100%',
  padding: '10px',
  border: `1px dashed ${$isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'}`,
  borderRadius: '8px',
  background: 'transparent',
  color: $isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  transition: transitionPresets.allFast,

  '&:hover': {
    background: $isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
    borderColor: $isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)',
    color: $isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
  },
}));

const EmptyState = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  color: $isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
  textAlign: 'center',
}));

// ============================================================================
// Component
// ============================================================================

export function KanbanColumn<T>({
  column,
  items,
  renderCard,
  onCardClick,
  neonAccent,
  showSLA = true,
  showCount = true,
  allowAdd = false,
  onAddCard,
}: KanbanColumnProps<T>) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: {
      type: 'column',
      column,
    },
  });

  // Use column color or provided neonAccent
  const effectiveAccent = neonAccent || column.color;

  // Check WIP limit
  const isOverWipLimit = column.limit ? items.length > column.limit : false;
  const wipText = column.limit ? `${items.length}/${column.limit}` : undefined;

  // Get item IDs for sortable context
  const itemIds = items.map((item) => item.id);

  return (
    <StyledColumn
      ref={setNodeRef}
      $isDark={isDark}
      $neonAccent={effectiveAccent}
      $isOver={isOver}
      $isOverWipLimit={isOverWipLimit}
    >
      {/* Column Header */}
      <ColumnHeader $neonAccent={effectiveAccent} $isDark={isDark} $isOverWipLimit={isOverWipLimit}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ColumnTitle
            sx={{
              color: effectiveAccent ? neonAccents[effectiveAccent].base : 'text.primary',
            }}
          >
            {column.title}
          </ColumnTitle>
          {wipText && <WipIndicator $isOver={isOverWipLimit}>{wipText}</WipIndicator>}
        </Box>

        {showCount && (
          <CountBadge $neonAccent={effectiveAccent} $isDark={isDark} $isOverLimit={isOverWipLimit}>
            {column.count}
          </CountBadge>
        )}
      </ColumnHeader>

      {/* Cards Container */}
      <CardsContainer>
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <AnimatePresence mode="popLayout">
            {items.length > 0 ? (
              items.map((item) => (
                <KanbanCard
                  key={item.id}
                  item={item}
                  renderContent={renderCard}
                  onClick={onCardClick ? () => onCardClick(item) : undefined}
                  neonAccent={effectiveAccent}
                  showSLA={showSLA}
                />
              ))
            ) : (
              <EmptyState $isDark={isDark}>
                <Typography variant="body2" sx={{ opacity: 0.6 }}>
                  No items
                </Typography>
              </EmptyState>
            )}
          </AnimatePresence>
        </SortableContext>

        {/* Add Card Button */}
        {allowAdd && onAddCard && (
          <AddCardButton $isDark={isDark} onClick={onAddCard} whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
            <span>+</span>
            <span>Add card</span>
          </AddCardButton>
        )}
      </CardsContainer>
    </StyledColumn>
  );
}

KanbanColumn.displayName = 'KanbanColumn';

export default KanbanColumn;
